import React, { useCallback, useEffect, useMemo, useState } from 'react';
import './style.less';
import { useUpdateUserOutboundSettings } from 'browser/components/Settings/Dialer/useUpdateUserOutboundSettings';
import { useSprings } from '@react-spring/core';
import { animated, config } from '@react-spring/web';
import { useDrag } from 'react-use-gesture';
import { Icon } from 'shared/components/Icon';
import { Switch } from 'shared/components/Switch';
import { Tooltip } from 'shared/components/Tooltip';
import { clamp } from 'lodash';
import { swap } from 'shared/utils/helpers';
import { Property } from 'types/property';

const lockedDialerColumns = ['status', 'contact.name', 'links', 'contact.phone'];

export const defaultDialerColumns = [
  /* Default enabled */
  {
    property: 'status',
    enabled: true,
  },
  {
    property: 'contact.name',
    enabled: true,
  },
  {
    property: 'links',
    enabled: true,
  },
  {
    property: 'contact.phone',
    enabled: true,
  },
  {
    property: 'contact.id',
    enabled: true,
  },
  {
    property: 'contact.title',
    enabled: true,
  },
  /* Default disabled */
  {
    property: 'contact.timezone',
    enabled: false,
  },
  {
    property: 'contact.company.name',
    enabled: false,
  },
  {
    property: 'contact.company.firstVisitDate',
    enabled: false,
  },
  {
    property: 'contact.company.lastVisitDate',
    enabled: false,
  },
  {
    property: 'contact.company.country',
    enabled: false,
  },
  {
    property: 'contact.company.location',
    enabled: false,
  },
  {
    property: 'contact.company.domain',
    enabled: false,
  },
  {
    property: 'contact.company.sizeEstimateHigh',
    enabled: false,
  },
  {
    property: 'contact.company.revenue',
    enabled: false,
  },
  {
    property: 'contact.company.utmSource',
    enabled: false,
  },
  {
    property: 'contact.company.utmMedium',
    enabled: false,
  },
  {
    property: 'contact.company.utmCampaign',
    enabled: false,
  },
  {
    property: 'contact.company.utmContent',
    enabled: false,
  },
  {
    property: 'contact.company.utmTerm',
    enabled: false,
  },
  {
    property: 'contact.company.industry',
    enabled: false,
  },
  {
    property: 'contact.email',
    enabled: false,
  },
  {
    property: 'contact.source',
    enabled: false,
  },
  {
    property: 'stats',
    enabled: false,
  },
];

const columnLabels: Record<string, string> = {
  status: 'Call Status',
  'contact.name': 'Name',
  links: 'Links',
  'contact.phone': 'Phone number',
  'contact.id': 'Last activity',
  'contact.title': 'Title',
  'contact.timezone': 'Time',
  'contact.company.name': 'Company',
  'contact.company.firstVisitDate': 'First seen',
  'contact.company.lastVisitDate': 'Last seen',
  'contact.company.country': 'Country',
  'contact.company.location': 'Location',
  'contact.company.domain': 'Company Website',
  'contact.company.sizeEstimateHigh': 'Company Size',
  'contact.company.revenue': 'Est. Revenue',
  'contact.company.utmSource': 'UTM Source',
  'contact.company.utmMedium': 'UTM Medium',
  'contact.company.utmCampaign': 'UTM Campaign',
  'contact.company.utmContent': 'UTM Content',
  'contact.company.utmTerm': 'UTM Term',
  'contact.company.industry': 'Industry',
  'contact.email': 'Email',
  'contact.source': 'Source',
  stats: 'Dial Stats',
};

const ITEM_HEIGHT = 40;

const animate = (
  order: number[],
  active = false,
  originalIndex = 0,
  curIndex = 0,
  y = 0,
) => (index: number) =>
  active && index === originalIndex
    ? {
        y: curIndex * ITEM_HEIGHT + y,
        scale: 1.04,
        zIndex: 1,
        immediate: (key: string) => key === 'zIndex',
        config: (key: string) => (key === 'y' ? config.stiff : config.default),
      }
    : {
        y: order.indexOf(index) * ITEM_HEIGHT,
        scale: 1,
        zIndex: 0,
        immediate: false,
      };

const extendWithCustomProperties = (
  properties: { property: string; enabled: boolean }[],
  customProperties: {
    company: Property[];
    contact: Property[];
  },
) => {
  const companyProperties = customProperties.company.map(p => {
    const property = `contact.company.customMetadata.${p.key}`;

    const found = properties.find(c => c.property === property);

    if (found) return false;

    return {
      property,
      enabled: false,
    };
  });

  const contactProperties = customProperties.contact.map(p => {
    const property = `contact.customMetadata.${p.key}`;

    const found = properties.find(c => c.property === property);

    if (found) return false;

    return {
      property,
      enabled: false,
    };
  });

  return [...properties, ...companyProperties, ...contactProperties].filter(Boolean) as {
    property: string;
    enabled: boolean;
  }[];
};

export const CallTaskDisplayColumnsPopover: React.FC<{
  customProperties: {
    company: Property[];
    contact: Property[];
  };
}> = ({ customProperties }) => {
  const { settings, updateSettings } = useUpdateUserOutboundSettings();

  const [columns, setColumns] = useState<{ property: string; enabled: boolean }[]>(
    extendWithCustomProperties(
      settings.outboundDialerColumns?.length
        ? settings.outboundDialerColumns
        : defaultDialerColumns,
      customProperties,
    ),
  );

  const [order, setOrder] = useState([...Array(columns.length).keys()]);
  const [springs, api] = useSprings(columns.length, animate(order));

  const bind = useDrag(({ args: [originalIndex], active, movement: [, y] }) => {
    const curIndex = order.indexOf(originalIndex);
    const curRow = clamp(
      Math.round((curIndex * ITEM_HEIGHT + y) / ITEM_HEIGHT),
      0,
      columns.length - 1,
    );
    const newOrder = swap(order, curIndex, curRow);

    api.start(animate(newOrder, active, originalIndex, curIndex, y));

    if (!active) {
      setOrder(newOrder);
      handleUpdate(newOrder.map(index => columns[index]));
    }
  });

  const handleUpdate = useCallback(
    c => {
      if (JSON.stringify(c) === JSON.stringify(settings.outboundDialerColumns)) {
        return;
      }

      const isColsDefault = JSON.stringify(c) === JSON.stringify(defaultDialerColumns);

      if (!settings.outboundDialerColumns?.length && isColsDefault) {
        return;
      }

      if (!!settings.outboundDialerColumns?.length && isColsDefault) {
        updateSettings('outboundDialerColumns', []);
        return;
      }

      updateSettings('outboundDialerColumns', c);
    },
    [settings.outboundDialerColumns, updateSettings],
  );

  useEffect(() => {
    handleUpdate(order.map(index => columns[index]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columns, updateSettings]);

  const extendedColumnLabels = useMemo(() => {
    return {
      ...columnLabels,
      ...customProperties.company.reduce((acc, p) => {
        acc[`contact.company.customMetadata.${p.key}`] = p.displayName;
        return acc;
      }, {} as Record<string, string>),
      ...customProperties.contact.reduce((acc, p) => {
        acc[`contact.customMetadata.${p.key}`] = p.displayName;
        return acc;
      }, {} as Record<string, string>),
    };
  }, [customProperties]);

  return (
    <div className="CallTaskDisplayColumnsPopover">
      <div
        className="CallTaskDisplayColumnsPopover__list"
        style={{ height: ITEM_HEIGHT * Object.keys(extendedColumnLabels).length }}
      >
        {springs.map(({ zIndex, scale, y }, index) => {
          const item = columns[index];

          return (
            <animated.div
              className="CallTaskDisplayColumnsPopover__list-item"
              key={item.property}
              style={{ zIndex, y, scale }}
            >
              <Icon
                {...bind(index)}
                icon="drag"
                width={16}
                height={16}
                className="CallTaskDisplayColumnsPopover__handle"
              />
              <label className="CallTaskDisplayColumnsPopover__label">
                <Tooltip
                  theme="dark"
                  title={item.enabled ? `Hide Column` : `Show Column`}
                >
                  <Switch
                    size="small"
                    className="CallTaskDisplayColumnsPopover__switch"
                    checked={item.enabled}
                    onChange={checked => {
                      setColumns(
                        columns.map(c => {
                          if (c.property === item.property) {
                            return {
                              ...c,
                              enabled: checked,
                            };
                          }

                          return c;
                        }),
                      );
                    }}
                    disabled={lockedDialerColumns.includes(item.property)}
                  />
                </Tooltip>
                <span>{extendedColumnLabels[item.property]}</span>
              </label>
              {lockedDialerColumns.includes(item.property) && (
                <Tooltip
                  theme="dark"
                  title={
                    <div className="CallTaskDisplayColumnsPopover__tooltip">
                      Default field. <br /> Cannot be hidden.
                    </div>
                  }
                >
                  <Icon
                    className="CallTaskDisplayColumnsPopover__lock"
                    width={16}
                    height={16}
                    icon="lock"
                  />
                </Tooltip>
              )}
            </animated.div>
          );
        })}
      </div>
    </div>
  );
};
